import React, { useMemo } from "react";
import "./Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const Terms = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients worldwide",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        {/* Header */}
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Terms of use</p>
           {/* <div className="pp-header-main"> */}
          <h1>Terms &amp; Conditions</h1>
         

          <p className="pp-meta">
            <span>Provider: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
          </p>
        </header>

        {/* Content */}
        <div className="privacy-policy-content">
          <section>
            <h2>1. Scope of these Terms</h2>
            <p>
              These Terms apply to your use of our website, our communication
              channels, and any services we deliver directly to you (for
              example, strategy, design, development, or marketing).
            </p>
            <p>
              In many cases we will also provide a proposal, statement of work,
              or service agreement for a specific project. If there is any
              inconsistency between that document and these Terms, the signed
              project document will take priority for that particular
              engagement.
            </p>
          </section>

          <section>
            <h2>2. Website use &amp; content</h2>
            <p>
              You agree to use our website in a lawful, respectful manner and
              not to attempt to damage, disable, or interfere with its normal
              operation or other visitors&apos; experience.
            </p>
            <p>
              All text, visuals, layouts, code, and other materials on this site
              are owned or licensed by {brand.name}. You may view, link to, and
              share our content for personal or informational purposes, but you
              may not copy, reproduce, resell, or present it as your own
              without our prior written permission.
            </p>
          </section>

          <section>
            <h2>3. Proposals, scope &amp; revisions</h2>
            <p>
              Before starting work, we will usually confirm scope, timelines,
              deliverables, and fees in writing (for example via proposal, email
              confirmation, or contract). Only what is clearly agreed in writing
              is considered &quot;in scope&quot;.
            </p>
            <p>
              If you request additional features, pages, integrations, or other
              changes after the scope has been agreed, we may:
            </p>
            <ul>
              <li>Provide a revised quote or change order; or</li>
              <li>Treat the extra work as a separate phase or mini-project.</li>
            </ul>
            <p>
              We will always communicate clearly about scope changes and related
              budget or timeline adjustments.
            </p>
          </section>

          <section>
            <h2>4. Your responsibilities</h2>
            <p>To help us deliver good work on time, you agree to:</p>
            <ul>
              <li>
                Provide timely access to accounts, tools, documents, and
                stakeholders we reasonably need.
              </li>
              <li>
                Supply content (such as copy, images, brand assets) in agreed
                formats and within agreed timeframes.
              </li>
              <li>
                Review and approve work within reasonable time and give clear,
                consolidated feedback.
              </li>
              <li>
                Ensure that all materials you provide are accurate, lawful, and
                do not infringe any third-party rights.
              </li>
            </ul>
            <p>
              Delays in feedback, content, or access may impact project
              timelines and may require us to adjust schedules, launch dates,
              and/or fees.
            </p>
          </section>

          <section>
            <h2>5. Fees, payments &amp; refunds</h2>
            <p>
              Our fees, payment schedule, and currency are set out in your
              proposal or agreement. Unless stated otherwise:
            </p>
            <ul>
              <li>
                Invoices are due within the payment term specified on the
                invoice.
              </li>
              <li>
                We reserve the right to pause work if payments become
                significantly overdue, after reasonable notice.
              </li>
              <li>
                Deposits, retainers, and fees for work already completed are
                generally non-refundable.
              </li>
            </ul>
            <p>
              Third-party costs (such as ad spend, stock assets, hosting,
              domains, or software subscriptions) are usually separate from our
              service fees and are either paid directly by you or re-billed to
              you as agreed.
            </p>
          </section>

          <section>
            <h2>6. Intellectual property &amp; usage rights</h2>
            <p>
              {brand.name} retains ownership of our pre-existing know-how,
              frameworks, processes, code libraries, and internal tools used
              during a project. These are not transferred to you unless we agree
              otherwise in writing.
            </p>
            <p>
              Once all agreed fees have been paid in full, you receive a
              license to use the final deliverables described in your agreement
              (for example, website designs, copy, or code) for your own
              business purposes.
            </p>
            <p>
              You may not resell our work as a template, theme, white-label
              product, or mass-distributed asset without our explicit written
              permission.
            </p>
          </section>

          <section>
            <h2>7. Third-party services &amp; dependencies</h2>
            <p>
              Many projects rely on third-party services such as hosting
              providers, payment gateways, analytics tools, email platforms,
              marketing software, or AI tools.
            </p>
            <p>
              These services are governed by their own terms, privacy policies,
              pricing, and availability. You are responsible for reviewing and
              accepting those terms as needed. {brand.name} cannot guarantee the
              performance, uptime, or future pricing of third-party providers
              and is not liable for issues caused by them, though we will
              assist with reasonable troubleshooting where possible.
            </p>
          </section>

          <section>
            <h2>8. Confidentiality</h2>
            <p>
              Both you and {brand.name} agree to keep each other&apos;s
              non-public information confidential. This includes (but is not
              limited to) business plans, credentials, internal processes,
              customer data, and any other information identified as
              confidential or reasonably understood to be confidential.
            </p>
            <p>
              We may share necessary information with trusted team members,
              subcontractors, or vendors solely for the purpose of delivering
              your project, and we expect them to follow similar confidentiality
              obligations. These confidentiality obligations continue even after
              a project ends.
            </p>
          </section>

          <section>
            <h2>9. Use of AI &amp; automation tools</h2>
            <p>
              Where appropriate, {brand.name} may use reputable AI or automation
              tools to assist with tasks such as ideation, drafting, testing,
              or optimization. We remain responsible for the final output we
              deliver to you under the agreed scope.
            </p>
            <p>
              You acknowledge that AI-generated content and insights may have
              limitations and should not be treated as legal, financial, or tax
              advice. You are responsible for reviewing and approving any
              content before publishing or relying on it.
            </p>
          </section>

          <section>
            <h2>10. Disclaimers</h2>
            <p>
              We aim to deliver thoughtful, professional work based on your
              goals and the information available at the time. However, outcomes
              in areas such as search rankings, traffic, conversions, or revenue
              depend on many factors beyond our control (including competition,
              algorithms, market conditions, budgets, and your internal
              implementation).
            </p>
            <p>
              Accordingly, {brand.name} does not guarantee specific business
              results, and nothing on this website or in our communications
              should be considered legal, financial, or tax advice.
            </p>
          </section>

          <section>
            <h2>11. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, {brand.name} and its team
              shall not be liable for any indirect, incidental, special,
              punitive, or consequential damages (including loss of profits,
              revenue, or data) arising out of or in connection with your use of
              our website or services.
            </p>
            <p>
              In any case, our total aggregate liability for all claims related
              to a specific project or service is limited to the total fees you
              paid to us for that project or service in the three (3) months
              immediately preceding the event giving rise to the claim.
            </p>
          </section>

          <section>
            <h2>12. Term, scheduling &amp; termination</h2>
            <p>
              The length of our engagement, milestones, and delivery dates will
              be outlined in your proposal or agreement. Timelines are estimates
              and may shift if scope changes or dependencies are delayed.
            </p>
            <p>
              Either party may usually end an engagement with written notice,
              subject to any notice period defined in the agreement. You remain
              responsible for paying for:
            </p>
            <ul>
              <li>Work completed up to the effective termination date; and</li>
              <li>
                Any non-cancellable third-party costs or commitments made on
                your behalf.
              </li>
            </ul>
          </section>

          <section>
            <h2>13. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time to reflect changes in
              our services, processes, or applicable law. When we make
              meaningful updates, we will adjust the &quot;Last updated&quot;
              date at the top of this page.
            </p>
            <p>
              Your continued use of our website or services after the revised
              Terms are posted will be considered acceptance of those changes.
            </p>
          </section>

          <section>
            <h2>14. Governing law &amp; disputes</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which{" "}
              {brand.name} is established, unless another jurisdiction is
              specified in a signed agreement between us.
            </p>
            <p>
              Any disputes arising out of or relating to these Terms, our
              website, or our services will be subject to the exclusive
              jurisdiction of the courts in that location, unless we agree
              otherwise in writing.
            </p>
          </section>

          <section>
            <h2>15. Privacy &amp; data protection</h2>
            <p>
              For details on how we collect, use, and protect personal data,
              please also review our{" "}
              <a href="/privacy-policy">Privacy Policy</a>. That document
              explains what information we process, why we process it, and the
              rights you may have in relation to your data.
            </p>
            <p>
              These Terms and our Privacy Policy work together to explain how we
              operate and how your information is handled when you interact with{" "}
              {brand.name}.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              If you have questions about these Terms, want to clarify how they
              apply to your project, or wish to raise a concern, you can reach
              us at:
            </p>
            <ul className="pp-contact-list">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                  {brand.email}
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                  {brand.phone}
                </a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Terms;
